import React, { useState, useEffect } from 'react';
import { useCMS } from '../context/CMSContext';

export default function Gallery({ gallerySlug }) {
  const { getGallery } = useCMS();
  const gallery = getGallery(gallerySlug);
  const [activeIndex, setActiveIndex] = useState(null);

  // Luk lightbox med Esc, skift med piletaster
  useEffect(() => {
    if (activeIndex === null) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setActiveIndex(null);
      } else if (e.key === 'ArrowRight') {
        handleNext();
      } else if (e.key === 'ArrowLeft') {
        handlePrev();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeIndex, gallery]);

  if (!gallery || !gallery.images || gallery.images.length === 0) {
    return null;
  }

  const { images, title, description } = gallery;
  const titleText = title?.da || 'Galleri';
  const descText = description?.da || '';

  const handlePrev = () => {
    setActiveIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setActiveIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  // Hjælper til Cloudflare on-the-fly resizing af billeder
  const getResizedUrl = (url, width = 600, quality = 80) => {
    if (!url.startsWith('https://assets.auxodev.dk')) return url;
    return url.replace(
      'https://assets.auxodev.dk/assets/',
      `https://assets.auxodev.dk/cdn-cgi/image/width=${width},quality=${quality},format=auto/assets/`
    );
  };

  return (
    <div className="my-12">
      <div className="text-center mb-8">
        <h3 className="text-2xl md:text-3xl font-serif text-brand-dark mb-2">{titleText}</h3>
        {descText && <p className="text-brand-brown text-sm max-w-xl mx-auto">{descText}</p>}
      </div>

      {/* Grid af billeder */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {images.map((img, index) => (
          <button
            key={index}
            onClick={() => setActiveIndex(index)}
            className="group relative aspect-[4/3] lg:aspect-auto lg:h-[450px] overflow-hidden rounded-brand bg-brand-sand/30 border border-brand-sand/50 transition-all duration-300 hover:border-brand-brown/50 focus:outline-none focus:ring-2 focus:ring-brand-brown focus:ring-offset-2 cursor-pointer"
            aria-label={`Vis billede ${index + 1} i fuld størrelse`}
          >
            <img
              src={getResizedUrl(img, 800, 80)}
              alt={`${titleText} - Billede ${index + 1}`}
              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-brand-dark/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
              <span className="bg-brand-cream/90 text-brand-dark text-xs uppercase tracking-wider px-3 py-1.5 rounded-brand shadow-sm">
                <i className="fa-regular fa-magnifying-glass-plus mr-1.5" /> Forstør
              </span>
            </div>
          </button>
        ))}
      </div>

      {/* Lightbox / Stor visning */}
      {activeIndex !== null && (
        <div
          className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-brand-dark/95 p-4 md:p-8"
          role="dialog"
          aria-modal="true"
        >
          {/* Luk-knap */}
          <button
            onClick={() => setActiveIndex(null)}
            className="absolute top-4 right-4 md:top-6 md:right-6 text-brand-cream/80 hover:text-brand-cream p-2 text-2xl transition-colors duration-200 cursor-pointer focus:outline-none"
            aria-label="Luk galleri"
          >
            <i className="fa-regular fa-xmark" />
          </button>

          {/* Hovedbillede beholder */}
          <div className="relative flex items-center justify-center w-full max-w-5xl h-[70vh] md:h-[80vh]">
            {/* Forrige knap */}
            <button
              onClick={handlePrev}
              className="absolute left-0 md:-left-16 text-brand-cream/80 hover:text-brand-cream p-3 text-3xl transition-colors duration-200 cursor-pointer focus:outline-none"
              aria-label="Forrige billede"
            >
              <i className="fa-regular fa-chevron-left" />
            </button>

            {/* Billedet */}
            <img
              src={getResizedUrl(images[activeIndex], 1600, 90)}
              alt={`${titleText} - Forstørret billede ${activeIndex + 1}`}
              className="max-w-full max-h-full object-contain rounded-brand select-none"
            />

            {/* Næste knap */}
            <button
              onClick={handleNext}
              className="absolute right-0 md:-right-16 text-brand-cream/80 hover:text-brand-cream p-3 text-3xl transition-colors duration-200 cursor-pointer focus:outline-none"
              aria-label="Næste billede"
            >
              <i className="fa-regular fa-chevron-right" />
            </button>
          </div>

          {/* Info i bunden */}
          <div className="mt-4 text-center text-brand-cream/80 text-sm">
            <span>Billede {activeIndex + 1} af {images.length}</span>
          </div>
        </div>
      )}
    </div>
  );
}
